import { useEffect, useState } from "react";
import {
  Row,
  Layout,
  Col,
  Button,
  Breadcrumb,
  Input,
  Tabs,
  Spin,
  Form,
  App,
} from "antd";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  UserOutlined,
  AppstoreOutlined,
  BarsOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "../tutorialpage/styles/index.less";

import { get_profile, get_role } from "../../services/user";
import { get_task_by_id } from "../../services/task";
import { team_getteamconfig, team_getMyTaskRole } from "../../services/team";
import { useTeamData } from "./hooks/useTeamData";
import { useTeamActions } from "./hooks/useTeamActions";
import { TeamModal } from "./components/TeamModal";
import { TeamTable } from "./components/TeamTable";
import { TeamCardView } from "./components/TeamCardView";
import { TeamDetailModal } from "./components/TeamDetailModal";
import { AddMemberModal } from "./components/AddMemberModal";
import { Team, ViewMode, TabKey, AvailableMember } from "./types";
import { useSocket } from "../../utils/socketio/SocketWrapper";

const { Search } = Input;
const { Content } = Layout;

const SUPER_ADMIN_ROLE_ID = '99999999-0000-0000-0000-999999999999';

const TEAM_MEMBER_SOCKET_EVENT = "team_member_changed";
const getTaskRoomName = (taskId: string) => `task:${taskId}`;

const Teampage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { socket, isConnected } = useSocket() as any;

  const [currentUser, setCurrentUser] = useState<any>(null);
  const [userRole, setUserRole] = useState<string>("User");
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [task, setTask] = useState<any>({ name: "test", submission_type: 1 });
  const [loading, setLoading] = useState(true);
  const [availableMembers, setAvailableMembers] = useState<AvailableMember[]>(
    [],
  );
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tabKey, setTabKey] = useState<TabKey>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [searchText, setSearchText] = useState("");
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [currentUserTaskRole, setCurrentUserTaskRole] = useState<number | null>(null);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [teamConfig, setTeamConfig] = useState<{
    min_size: number;
    team_size: number;
    teamname_regex: string | null;
  }>({ min_size: 1, team_size: 15, teamname_regex: null });

  const isSuperAdmin = currentUser?.role_id === SUPER_ADMIN_ROLE_ID;
  const isTaskAdmin = currentUserTaskRole === 2;
  const isPrivilegedUser = isSuperAdmin || isTaskAdmin;

  // Use custom hooks
  const {
    teams,
    loading: teamsLoading,
    refreshData,
    updateTeamStatus,
  } = useTeamData({
    taskId: params.task_id || "",
    tabKey,
    currentUserId: currentUser?.user_id,
    currentUserRoleId: currentUser?.role_id,
    searchQuery: searchText,
  });

  const handleExportUser = () => {
    let csvContent = '';
    if (tabKey === 'user') {
      csvContent = 'Name,Email,Team Name,Team Role\n';
      teams.forEach((member) => {
        const m = member.members[0];
        const teamRole = m?.role === 1 ? 'Leader' : m?.role === 0 ? 'Member' : '-';
        csvContent += `"${member.name}","${m?.email || ''}","${(member as any).team_name || '-'}","${teamRole}"\n`;
      });
    } else {
      csvContent = 'Team Name,Leader Email,Total Members\n';
      teams.forEach((team) => {
        const leader = team.members.find((m) => m.role === 1);
        csvContent += `"${team.name}","${leader?.email || ''}","${team.members.length}"\n`;
      });
    }
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `export_${tabKey === 'user' ? 'members' : 'teams'}_${params.task_id}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const { saveTeam, deleteTeam, leaveTeam, removeMemberByAdmin, fetchAvailableMembers } =
    useTeamActions({
      taskId: params.task_id || "",
      form,
      onSuccess: () => {
        refreshData();
        setIsModalOpen(false);
      },
      isPrivilegedUser: !!isPrivilegedUser,
      isTaskAdmin,
    });

  const openModal = async (team?: Team) => {
    // ตรวจสอบว่ามี currentUser หรือไม่ ถ้าไม่มีให้รอก่อน
    if (!currentUser && !team) {
      message.warning("กำลังโหลดข้อมูลผู้ใช้ กรุณารอสักครู่...");
      return;
    }

    setEditingTeam(team || null);

    // Fetch available members
    setLoadingMembers(true);
    const members = await fetchAvailableMembers();
    setAvailableMembers(members);
    setLoadingMembers(false);

    // Set form values
    if (!team) {
      if (currentUser) {
        const initialMember = {
          id: currentUser.user_id,
          user_id: currentUser.user_id,
          name:
            currentUser.username ||
            currentUser.name ||
            currentUser.display_name,
          email: currentUser.email,
          role: 1, // Leader
        };

        form.setFieldsValue({
          name: "",
          members: [initialMember],
        });
      } else {
        message.error("ไม่พบข้อมูลผู้ใช้ กรุณารีเฟรชหน้าใหม่");
        return;
      }
    } else {
      const mappedMembers = team.members.map((member) => ({
        ...member,
        user_id: member.user_id || member.id,
      }));

      form.setFieldsValue({
        name: team?.name || "",
        members: mappedMembers,
      });
    }

    setIsModalOpen(true);
  };

  useEffect(() => {
    if (editingTeam) {
      form.setFieldsValue({
        name: editingTeam.name,
        members: editingTeam.members || [],
      });
    } else {
      form.resetFields();
    }
  }, [editingTeam, form]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await getProfile();
      await getUserRole();
      await getTaskDetail();
      await getTeamConfig();
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (params.task_id) {
      team_getMyTaskRole({ task_id: params.task_id }).then((res) => {
        if (res.status === "success") setCurrentUserTaskRole(res.task_role ?? null);
      }).catch(() => {});
    }
  }, [params.task_id]);

  const getUserRole = async () => {
    try {
      const res = await get_role();
      if (res.result && res.result.length > 0) {
        setUserRole(res.result[0].role_name);
      }
    } catch (error) {
      console.error("Error fetching user role:", error);
      setUserRole("User");
    }
  };

  useEffect(() => {
    const state = location.state as {
      mode?: string;
      shouldOpenModal?: boolean;
    };
    if (state?.shouldOpenModal && state?.mode === "create") {
      // รอให้ currentUser พร้อมก่อนเปิด Modal
      if (currentUser) {
        openModal();
        window.history.replaceState({}, document.title);
      }
    }
  }, [location, currentUser]);

  useEffect(() => {
    const currentTaskId = params.task_id;
    const currentSocket = socket?.current;

    if (!currentTaskId || !currentSocket || !isConnected) {
      return;
    }

    const roomName = getTaskRoomName(currentTaskId);
    const handleTeamMemberChanged = (data: any) => {
      // console.log("[team socket] receive", data);
      if (data?.task_id !== currentTaskId) {
        return;
      }

      refreshData();
    };

    // console.log("[team socket] join_room", {
    //   taskId: currentTaskId,
    //   roomName,
    //   socketId: currentSocket.id,
    // });
    currentSocket.emit("join_room", roomName);
    currentSocket.on(TEAM_MEMBER_SOCKET_EVENT, handleTeamMemberChanged);

    return () => {
      // console.log("[team socket] leave_room", {
      //   taskId: currentTaskId,
      //   roomName,
      //   socketId: currentSocket.id,
      // });
      currentSocket.off(TEAM_MEMBER_SOCKET_EVENT, handleTeamMemberChanged);
      currentSocket.emit("leave_room", roomName);
    };
  }, [isConnected, params.task_id, refreshData, socket]);

  const getProfile = async () => {
    try {
      const res = await get_profile();
      const user = res.result;
      if (!user || user.length === 0 || !user[0]) {
        navigate("/");
        return;
      }
      setCurrentUser(user[0]);
    } catch (error) {
      console.error(error);
      navigate("/");
    }
  };

  const getTaskDetail = async () => {
    try {
      const res = await get_task_by_id({ task_id: params.task_id });
      if (
        !res.result ||
        !Array.isArray(res.result) ||
        res.result.length === 0
      ) {
        navigate("/404");
        return;
      }
      const taskData = res.result[0];
      setTask(taskData);
    } catch (error) {
      console.error(error);
      navigate("/404");
    }
  };

  const getTeamConfig = async () => {
    try {
      const res = await team_getteamconfig({ task_id: params.task_id });
      if (res.status === "success" && res.result && res.result.length > 0) {
        const config = res.result[0];
        setTeamConfig({
          min_size: config.min_size ?? 1,
          team_size: config.team_size ?? 15,
          teamname_regex: config.teamname_regex || null,
        });
      }
    } catch (error) {
      console.error("Error fetching team config:", error);
    }
  };

  const truncateText = (text: string, max: number) =>
    text.length > max ? `${text.substring(0, max)}...` : text;

  const canDeleteTeam = (team: Team): boolean => {
    if (isSuperAdmin || isTaskAdmin) return true;

    // หัวหน้าทีมสามารถลบทีมของตัวเองได้ (เฉพาะทีมที่มีสมาชิกคนเดียว)
    const isLeader = team.members.some((member) => {
      const memberId = member.user_id || member.id;
      return member.role === 1 && memberId === currentUser?.user_id;
    });
    return isLeader;
  };

  // ฟังก์ชันตรวจสอบว่า user สามารถแก้ไขทีมได้หรือไม่
  const canEditTeam = (team: Team): boolean => {
    if (isSuperAdmin || isTaskAdmin) return true;

    // หัวหน้าทีมสามารถแก้ไขทีมของตัวเองได้
    const isLeader = team.members.some((member) => {
      const memberId = member.user_id || member.id;
      return member.role === 1 && memberId === currentUser?.user_id;
    });
    return isLeader;
  };

  const handleDeleteTeam = (id: string) => {
    deleteTeam(id, refreshData);
  };

  const handleRemoveMember = (_teamId: string, userId: string) => {
    removeMemberByAdmin(userId, refreshData);
  };

  const handleLeaveTeam = (teamId: string) => {
    if (!currentUser?.user_id) {
      message.error("ไม่พบข้อมูลผู้ใช้");
      return;
    }
    leaveTeam(teamId, currentUser.user_id, refreshData);
  };

  const handleRowClick = (team: Team) => {
    setSelectedTeam(team);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedTeam(null);
  };

  // ฟังก์ชันตรวจสอบว่าสมาชิกสามารถออกจากทีมได้หรือไม่
  const canLeaveTeam = (team: Team): boolean => {
    if (!currentUser?.user_id) return false;

    // ตรวจสอบว่าผู้ใช้เป็นสมาชิกของทีมนี้หรือไม่
    const isMember = team.members.some((member) => {
      const memberId = member.user_id || member.id;
      return memberId === currentUser?.user_id;
    });

    if (!isMember) return false;

    // หัวหน้าทีมไม่สามารถออกจากทีมได้ (ต้องโอนหัวหน้าก่อน หรือลบทีม)
    const isLeader = team.members.some((member) => {
      const memberId = member.user_id || member.id;
      return member.role === 1 && memberId === currentUser?.user_id;
    });

    return !isLeader;
  };

  return (
    <Content className="tutorial-layout">
      <div>
        <Row justify="space-between" align="middle">
          <Col xs={24} md={12}>
            <Breadcrumb
              separator=">"
              items={[
                {
                  title: (
                    <a
                      style={{ fontWeight: "bold" }}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/task");
                      }}
                    >
                      {t("Task")}
                    </a>
                  ),
                },
                {
                  title: (
                    <a
                      style={{ fontWeight: "bold" }}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/task/detail/" + params.task_id);
                      }}
                    >
                      {truncateText(task.name, 20)}
                    </a>
                  ),
                },
                {
                  title: <span style={{ fontWeight: "bold" }}>Team</span>,
                },
              ]}
            />
          </Col>
          <Col xs={24} md={8}>
            <Row justify="end" gutter={[10, 10]} wrap={false}>
              <Col flex="auto">
                <Search
                  placeholder={t("Search")}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                />
              </Col>
              {isPrivilegedUser && (
                <Col>
                  <Button
                    icon={<DownloadOutlined />}
                    style={{ backgroundColor: "#237804", color: "white", borderColor: "#237804" }}
                    onClick={handleExportUser}
                  >
                    Export User
                  </Button>
                </Col>
              )}
              <Col>
                {tabKey === "all" ? (
                  <Button
                    type="primary"
                    icon={<UserOutlined />}
                    style={{ backgroundColor: "#00215E", color: "white" }}
                    onClick={() => openModal()}
                  >
                    + New Team
                  </Button>
                ) : isPrivilegedUser ? (
                  <Button
                    type="primary"
                    icon={<UserOutlined />}
                    style={{ backgroundColor: "#00215E", color: "white" }}
                    onClick={() => setIsAddMemberModalOpen(true)}
                  >
                    + Add Member
                  </Button>
                ) : null}
              </Col>
            </Row>
          </Col>
        </Row>
        <hr />
      </div>

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Tabs
            activeKey={tabKey}
            onChange={(key) => setTabKey(key as TabKey)}
            style={{ marginBottom: 0 }}
            items={[
              { key: "all", label: "All Teams" },
              { key: "user", label: "All Member" },
            ]}
          />
        </Col>
        {tabKey !== "user" && (
          <Col>
            <Row gutter={8}>
              <Col>
                <Button
                  style={{
                    borderColor: "#91caff",
                    backgroundColor:
                      viewMode === "table" ? "#e6f4ff" : "#ffffff",
                  }}
                  icon={<BarsOutlined style={{ color: "#0958d9" }} />}
                  onClick={() => setViewMode("table")}
                />
              </Col>
              <Col>
                <Button
                  style={{
                    borderColor: "#91caff",
                    backgroundColor:
                      viewMode === "card" ? "#e6f4ff" : "#ffffff",
                  }}
                  icon={<AppstoreOutlined style={{ color: "#0958d9" }} />}
                  onClick={() => setViewMode("card")}
                />
              </Col>
            </Row>
          </Col>
        )}
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          <Spin spinning={teamsLoading}>
            {tabKey === "user" || viewMode === "table" ? (
              <TeamTable
                teams={teams}
                tabKey={tabKey}
                taskId={params.task_id || ""}
                onEdit={openModal}
                onDelete={handleDeleteTeam}
                onLeave={handleLeaveTeam}
                onRemoveMember={handleRemoveMember}
                onRowClick={handleRowClick}
                onStatusChange={updateTeamStatus}
                onRefresh={refreshData}
                canDeleteTeam={canDeleteTeam}
                canEditTeam={canEditTeam}
                canLeaveTeam={canLeaveTeam}
                currentUser={currentUser}
                isTaskAdmin={isTaskAdmin}
              />
            ) : (
              <TeamCardView
                teams={teams}
                onEdit={openModal}
                onDelete={handleDeleteTeam}
                onLeave={handleLeaveTeam}
                onCardClick={handleRowClick}
                canDeleteTeam={canDeleteTeam}
                canEditTeam={canEditTeam}
                canLeaveTeam={canLeaveTeam}
              />
            )}
          </Spin>
        </div>
      )}

      <TeamModal
        isOpen={isModalOpen}
        editingTeam={editingTeam}
        form={form}
        currentUser={currentUser}
        availableMembers={availableMembers}
        loadingMembers={loadingMembers}
        teamConfig={teamConfig}
        taskId={params.task_id || ""}
        onSave={() => saveTeam(editingTeam, teamConfig)}
        isTaskAdmin={isTaskAdmin}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
          setEditingTeam(null);
        }}
      />

      <TeamDetailModal
        isOpen={isDetailModalOpen}
        team={selectedTeam}
        onClose={handleCloseDetailModal}
      />

      <AddMemberModal
        isOpen={isAddMemberModalOpen}
        taskId={params.task_id || ""}
        onSuccess={() => {
          setIsAddMemberModalOpen(false);
          refreshData();
        }}
        onCancel={() => setIsAddMemberModalOpen(false)}
      />
    </Content>
  );
};

export default Teampage;
